<script lang="ts" setup>
import { computed } from 'vue';
import Chip from 'primevue/chip';
import { PersonBuildingInterface } from '@/types/BuildingInterface';
import { PERMISSIONS, EMIT } from '@/enum';
import { can } from '@/composables';

type BuildingInterface = {
  organization_id: number;
  organization_name: string;
  buildings: PersonBuildingInterface[];
};

type organizationInterface = {
  id: number;
  name: string;
  parent_id: number | null;
};

const props = defineProps<{
  flattenOrganizations: organizationInterface[];
  buildings: PersonBuildingInterface[];
  totalRecords: number;
  loading: boolean;
}>();

const emit = defineEmits([EMIT.EDIT]);

const groupByOrganization = (buildings) => {
  const uniqueOrganizations = [...new Map(props.flattenOrganizations.map((org) => [org.id, org])).values()];

  return uniqueOrganizations.map((org) => {
    const orgBuildings = buildings.filter((b) => b.organization_id === org.id);
    const uniqueBuildings = [...new Map(orgBuildings.map((b) => [b.building_id, b])).values()];

    return {
      organization_id: org.id,
      organization_name: org.name,
      buildings: uniqueBuildings.map((b) => ({
        id: b.id,
        building_id: b.building_id,
        building_name: b.building_name,
        person_id: b.person_id,
      })),
    };
  });
};

const grouped = computed(() => groupByOrganization(props.buildings));

const onEdit = (buildingToEdit: BuildingInterface) => {
  emit(EMIT.EDIT, buildingToEdit);
};
</script>

<template>
  <Card>
    <template #header>
      <div class="flex items-center justify-between w-full">
        <h2 class="text-xl font-semibold">Buildings</h2>
        <Chip>{{ props.totalRecords }}</Chip>
      </div>
    </template>

    <template #content>
      <DataTable
        :value="grouped"
        :loading="props.loading"
        data-Key="organization_id"
        responsive-layout="scroll"
        :sort-field="'organization_name'"
        :sort-order="1"
      >
        <Column field="organization_name" header="Organization" sortable />
        <Column field="buildings" header="Buildings">
          <template #body="{ data }">
            <ul v-if="data.buildings.length" style="list-style: none; padding: 0; margin: 0">
              <li v-for="building in data.buildings" :key="building.building_id">
                {{ building.building_name }}
              </li>
            </ul>
            <span v-else>Buildings not assigned</span>
          </template>
        </Column>
        <Column v-if="can([PERMISSIONS.BUILDING_ASSIGN])" field="actions" header="Actions">
          <template #body="{ data }">
            <a class="flex items-center space-x-2" @click="onEdit(data)">
              <Icon name="edit" class="text-lg text-blue-500" />
              <span class="text-sm text-gray-800">Edit</span>
            </a>
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
}
</style>
